import { AvailableModules } from 'generated/featureDefinitions'
import type { AllFeatureIds } from 'generated/featureIds.generated'

import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import hasFeature from 'modules/core/lib/hasFeature'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

import { useAttendanceFeatures } from './attendanceFeaturesContext'

const featureFlagMappings: Record<string, AllFeatureIds> = {
  time_tracking_management: 'time_tracking',
  time_tracking_multi_policies: 'time_tracking_multiple_policies',
  time_tracking_automatic_breaks: 'attendance_automatic_breaks',
  time_tracking_automatic_planned_breaks: 'time_tracking_automatic_planned_breaks',
  time_tracking_overtime_requests: 'time_tracking_overtime_approval',
  time_tracking_ranges_and_tolerances: 'time_tracking_tolerance',
  time_tracking_face_recognition: 'face_recognition',
  time_tracking_geofencing_alerts: 'dev_locations_geofencing',
  time_tracking_autofill_timesheet: 'time_tracking_autofill_timesheet',
  time_tracking_edit_timesheet_authorization: 'time_tracking_edit_timesheet_authorization',
  time_tracking_time_alerts: 'dev_time_tracking_inconsistencies',
  payroll_bundle: 'payroll_bundle', // used in payroll compensation,
  time_tracking_corrective_breaks: 'time_tracking_corrective_breaks',
}

export const useTimeTrackingFeatureAvailability = (feature_id: string): boolean => {
  const { loading: featureLoading, featureAvailability: ttFeatureAvailability } =
    useAttendanceFeatures(feature_id)

  if (hasFeature('dev_packaging_paywalls')) {
    return !featureLoading && ttFeatureAvailability
  } else {
    const mappedFeature = featureFlagMappings[feature_id]
    return mappedFeature ? hasFeature(mappedFeature) : false
  }
}

// still used outside attendance module
// app_store
// modules/app_store/pages/SettingsPage/ActionWrapper/TimeTrackingButton/index.tsx
// modules/app_store/pages/SettingsPage/Apps/TimeTracking/Settings/index.tsx
const useDeprecatedTimeTrackingFeatureAvailability = (
  company_id: number,
  feature_id: string
): boolean => {
  const { loading: featureLoading, featureAvailability: ttFeatureAvailability } =
    useFeatureAvailability(feature_id, company_id)

  if (hasFeature('dev_packaging_paywalls')) {
    return !featureLoading && ttFeatureAvailability.available
  } else {
    const mappedFeature = featureFlagMappings[feature_id]
    return mappedFeature ? hasFeature(mappedFeature) : false
  }
}

export const useTimeTrackingFeatureAvailabilityForCurrentCompany = (feature_id: string) => {
  return useTimeTrackingFeatureAvailability(feature_id)
}

export const timeTrackingModuleEnabledInAvailabilities = (
  moduleAvailabilities: ModuleAvailabilityType[]
) => {
  if (hasFeature('dev_packaging_paywalls')) {
    return isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.time_tracking)
  } else {
    return hasFeature('time_tracking')
  }
}
